import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  TrendingUp, Users, Eye, Layers, Clock, Wallet, HelpCircle, Rocket, Handshake, Building2,
  Compass, Map, Cog, Landmark, LineChart, Database, Sparkles, Award, Scale, Target, Globe,
  Activity, Search,
} from "lucide-react";
import Seo from "@/components/common/Seo";
import { Reveal, KineticHeading, KineticHeadingInView } from "@/components/common/Reveal";
import CTASection from "@/components/common/CTASection";
import StrategyNetwork from "@/components/heroes/CoreSpine";

const CHALLENGES = [
  { icon: TrendingUp, title: "ERP no longer supporting business growth", body: "The core platform was built for a smaller, simpler business, and is starting to cap ambition rather than support it." },
  { icon: Users, title: "Poor user adoption", body: "Investment has been made, but the business quietly runs on workarounds outside the system." },
  { icon: Eye, title: "Limited executive visibility", body: "Leadership cannot see the business through the platform as clearly as decisions now require." },
  { icon: Layers, title: "Disconnected business processes", body: "Work fragments across systems, functions and geographies that no longer speak to each other cleanly." },
  { icon: Clock, title: "Delayed ERP projects", body: "Programmes slip past budget and schedule, and the business begins to lose confidence in the outcome." },
  { icon: Wallet, title: "Rising ERP costs", body: "Cost of ownership keeps climbing while the link to business value stays hard to defend." },
  { icon: HelpCircle, title: "Upgrade uncertainty", body: "The right time, right path and right partner for the next upgrade are not clear enough to commit to." },
  { icon: Rocket, title: "S/4HANA planning", body: "The move to S/4HANA is on the agenda, and leadership needs an independent view of the case and the plan." },
  { icon: Handshake, title: "Vendor dependence", body: "Critical decisions are being shaped by the vendor and its partners rather than by the business itself." },
  { icon: Building2, title: "Business expansion", body: "Growth into new sites, entities or countries is exposing gaps in the core platform and its governance." },
];

const AREAS = [
  { icon: Compass, title: "ERP Strategy", body: "A clear, board-ready position on what the ERP should be for the business over the next five to ten years." },
  { icon: Map, title: "SAP Roadmap", body: "A prioritised, defensible plan for what happens with SAP, and in what order, and why." },
  { icon: Cog, title: "Business Process Alignment", body: "The processes the platform is meant to serve, and the ones that need to change for value to actually land." },
  { icon: Landmark, title: "Executive Governance", body: "The oversight, decision rights and reporting that keep the programme accountable to the board." },
  { icon: LineChart, title: "Technology Investment Review", body: "An honest read on where the ERP investment has already gone, and what the business is receiving in return." },
  { icon: Database, title: "ERP Modernisation", body: "The choice of platform, partner and path, made with the confidence that comes from independence." },
  { icon: Sparkles, title: "S/4HANA Strategy", body: "A grounded view of when, how and at what scale the move to S/4HANA earns its place in the plan." },
  { icon: Handshake, title: "Vendor Evaluation", body: "Objective evaluation of platform vendors and implementation partners, free of commercial bias." },
  { icon: Search, title: "Business Case Development", body: "A defensible business case in the board's own language, not the vendor's marketing." },
  { icon: Activity, title: "Post-Implementation Review", body: "A candid read on what the programme actually delivered, and the two or three moves that will unlock the rest." },
];

const OUTCOMES = [
  { title: "Better Executive Decisions", note: "Sharper, faster calls at the top." },
  { title: "Operational Excellence", note: "A leaner, more resilient core." },
  { title: "Improved Visibility", note: "A clear board-level view of the business." },
  { title: "Investment Optimisation", note: "ERP spend tied to a defensible return." },
  { title: "Lower Risk", note: "Programme and platform risk controlled." },
  { title: "Business Growth", note: "New capacity, markets and revenue." },
  { title: "Better Governance", note: "Decisions held to account." },
  { title: "Scalable Operations", note: "A core built for the next stage." },
];

const WHY = [
  { icon: Award, title: "27+ years leadership", body: "Judgement built inside real ERP and platform programmes, on real business consequence, not theory." },
  { icon: Target, title: "Business-first thinking", body: "Every recommendation begins with the business result. Technology follows the strategy, never the other way around." },
  { icon: Scale, title: "Independent advice", body: "No products to sell and no platforms to push. The only interest is the ERP decision that is right for your business." },
  { icon: Handshake, title: "Vendor-neutral perspective", body: "Objective evaluation of platform vendors and implementation partners, free of commercial bias or hidden incentives." },
  { icon: Users, title: "Fractional CIO leadership", body: "Senior, board-level technology leadership on the cadence your business requires, without a permanent appointment." },
  { icon: Compass, title: "Executive technology strategy", body: "A grounded plan the business can genuinely execute, not a document that looks impressive and moves nothing." },
  { icon: Globe, title: "Experience across India, the UAE and the Gulf", body: "Direct experience of business conditions across the region, and how ERP decisions play out in practice." },
];

const serviceLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "ERP Strategy & SAP Executive Advisory",
  serviceType: "Executive ERP & SAP Advisory",
  description: "Independent, board-level advisory on ERP strategy, SAP roadmaps and enterprise application decisions. Aftab Azad works with executive teams in growing businesses across India, the UAE and the Gulf Region to ensure ERP investments create measurable business value.",
  provider: { "@type": "Person", name: "Aftab Azad" },
  areaServed: ["India", "United Arab Emirates", "Gulf Region", "Middle East", "Singapore", "Global"],
  url: "https://aftabazad.com/services/erp-sap-advisory",
};

export default function ERPStrategy() {
  return (
    <>
      <Seo
        title="ERP Strategy & SAP Executive Advisory"
        description="Independent executive advisory on ERP strategy, SAP roadmaps, S/4HANA planning and enterprise application decisions for growing businesses across India, the UAE and the Gulf Region."
        path="/services/erp-sap-advisory"
        keywords={["ERP Strategy", "SAP Executive Advisory", "SAP Advisor", "S/4HANA Strategy", "ERP Modernisation", "Fractional CIO", "Executive Technology Advisor", "Enterprise Applications Advisor"]}
        jsonLd={serviceLd}
      />

      {/* SECTION 1 — HERO */}
      <section data-testid="erp-hero" className="relative overflow-hidden bg-navy pb-24 pt-40 text-white grain-overlay md:pb-28 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[600px] w-[600px] rounded-full bg-royal/20 blur-[140px]" />
        <StrategyNetwork className="pointer-events-none absolute inset-y-10 right-0 hidden h-[calc(100%-5rem)] w-[38%] max-w-[520px] pr-4 opacity-90 md:block lg:w-[40%]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="font-body text-xs font-bold uppercase tracking-[0.3em] text-sky">ERP & SAP Advisory</p>
          <h1 className="mt-6 max-w-5xl font-heading text-4xl font-light leading-[1.04] tracking-tight md:text-[3.4rem]">
            <KineticHeading lines={["ERP Strategy &"]} lineClassName="font-light" />
            <KineticHeading lines={["SAP Executive Advisory."]} lineClassName="font-black text-sky" />
          </h1>
          <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-slate-300 md:text-xl">
            Enterprise applications should enable business growth, improve operational visibility and
            support better executive decision-making. Whether you are evaluating ERP modernisation,
            planning an SAP transformation or reviewing an existing investment, Aftab Azad helps
            leadership teams make practical, business-focused decisions that deliver measurable value.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/book" data-testid="erp-hero-book"
              className="group inline-flex items-center gap-2 rounded-full bg-royal px-8 py-4 font-body text-base font-semibold text-white transition-colors duration-300 hover:bg-sky hover:text-navy">
              Book Executive Strategy Session
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link to="/contact" data-testid="erp-hero-discuss"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-body text-base font-medium text-white transition-colors hover:border-sky hover:text-sky">
              Discuss Your ERP Roadmap
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-white/10 pt-6">
            {["27+ years executive leadership", "India", "UAE", "Gulf Region", "Global"].map((t, i, arr) => (
              <span key={t} className="flex items-center gap-3">
                <span className="font-body text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">{t}</span>
                {i < arr.length - 1 && <span className="h-1 w-1 rounded-full bg-sky/50" />}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — BUSINESS CHALLENGES */}
      <section data-testid="erp-challenges" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Business challenges</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Where the ERP", "conversation", "usually begins."]} />
            </h2>
          </div>
          <div className="mt-10 md:mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {CHALLENGES.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.title} delay={(i % 3) * 0.05}>
                  <div className="flex h-full flex-col rounded-[1.5rem] border border-slate-200 bg-white p-8">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy">
                      <Icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-heading text-lg font-bold leading-snug tracking-tight text-navy">{c.title}</h3>
                    <p className="mt-3 font-body text-sm leading-relaxed text-slate-600">{c.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3 — EXECUTIVE ADVISORY AREAS */}
      <section data-testid="erp-areas" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Executive advisory areas</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["The full range of", "the ERP question."]} />
            </h2>
          </div>
          <div className="mt-10 md:mt-12 grid gap-6 md:grid-cols-2">
            {AREAS.map((a, i) => {
              const Icon = a.icon;
              return (
                <Reveal key={a.title} delay={(i % 2) * 0.05}>
                  <div className="flex h-full gap-5 rounded-[1.5rem] border border-slate-200 bg-surface p-8">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                      <Icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold leading-snug tracking-tight text-navy">{a.title}</h3>
                      <p className="mt-3 font-body leading-relaxed text-slate-600">{a.body}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 4 — BUSINESS OUTCOMES */}
      <section data-testid="erp-outcomes" className="relative overflow-hidden bg-navy py-24 text-white md:py-32 grain-overlay">
        <div className="pointer-events-none absolute -left-32 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-royal/20 blur-[120px]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">Business outcomes</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight md:text-5xl">
              <KineticHeadingInView lines={["The value the", "board can see."]} />
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-[1.5rem] border border-white/10 bg-white/10 md:grid-cols-4">
            {OUTCOMES.map((o) => (
              <div key={o.title} className="bg-navy/80 px-6 py-8 backdrop-blur">
                <div className="font-heading text-lg font-semibold leading-snug tracking-tight text-white">{o.title}</div>
                <p className="mt-2 font-body text-sm leading-relaxed text-slate-400">{o.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — WHY WORK WITH AFTAB */}
      <section data-testid="erp-why" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Why business leaders choose Aftab Azad</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["The difference", "in the room."]} />
            </h2>
          </div>
          <div className="mt-10 md:mt-12 grid gap-x-12 gap-y-14 md:grid-cols-2">
            {WHY.map((w) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.title} className="flex gap-6 border-t border-slate-200 pt-8">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                    <Icon className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold tracking-tight text-navy">{w.title}</h3>
                    <p className="mt-3 max-w-md font-body text-lg leading-relaxed text-slate-600">{w.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 6 — CLOSING */}
      <section data-testid="erp-closing" className="relative overflow-hidden bg-navy py-24 text-white md:py-32 grain-overlay">
        <div className="pointer-events-none absolute -right-32 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-royal/20 blur-[120px]" />
        <div className="relative mx-auto max-w-4xl px-6 text-center md:px-12">
          <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">A closing thought</p>
          <blockquote className="mt-8 font-heading text-3xl font-light leading-[1.3] tracking-tight md:text-4xl">
            ERP is one of the most significant business investments an organisation ever makes. The
            objective is not simply to implement software. It is to create better business performance,
            through stronger processes, improved governance and sharper executive decision-making.
          </blockquote>
        </div>
      </section>

      <CTASection
        overline="Let's build an ERP strategy"
        heading={["That supports your", "business growth."]}
        body="Whether you are evaluating ERP modernisation, planning an SAP transformation or reviewing an existing investment, let's discuss how a practical, business-first ERP strategy can support your long-term growth."
        primary={{ label: "Schedule Executive Strategy Session", to: "/book" }}
        secondary={{ label: "Explore Advisory Services", to: "/services/executive-advisory" }}
      />
    </>
  );
}
